import React from 'react';
import { isMac } from '../../../../general/utils/isMac';
import { TM } from '../tokens';

// Autocomplete over the user's past prompts (see promptHistory.ts), opened from
// the prompt field with the keyboard or from the hint above it.
//
// The trigger is Ctrl+Space, the editor convention on Windows / Linux. On macOS
// that combination is taken by the system input-source switcher, so there the
// hint advertises ⌥Space (Option+Space) — the handler in CanvasPanel accepts
// either on every platform, so a muscle-memory press still works.
export const SUGGEST_HOTKEY_LABEL = isMac ? '⌥Space' : 'Ctrl+Space';

// Splits a suggestion around the first case-insensitive occurrence of what the
// user has typed, so the matched part can be emphasised in the row.
function highlight(text: string, query: string): [string, string, string] {
  const q = query.trim();
  if (!q) return [text, '', ''];
  const at = text.toLowerCase().indexOf(q.toLowerCase());
  if (at < 0) return [text, '', ''];
  return [text.slice(0, at), text.slice(at, at + q.length), text.slice(at + q.length)];
}

// The popover itself: up to PROMPT_SUGGEST_LIMIT single-line rows, each clipped
// with an ellipsis, sized to the prompt box it sits above. Rows are picked on
// mousedown (with the default prevented) so the textarea never loses focus —
// otherwise the field's blur would close the popover before a click landed.
//
// `items` may be empty: the user has past prompts, but none matching what is
// typed. The popover then says so instead of vanishing, which would read as a
// dead hotkey.
export const PromptSuggest: React.FC<{
  items: string[];
  activeIndex: number;
  query: string;
  onPick: (text: string) => void;
  onHover: (index: number) => void;
}> = ({ items, activeIndex, query, onPick, onHover }) => (
  <div className="al-magic-suggest" role="listbox" aria-label="Past prompts">
    <div className="al-magic-suggest__head">
      {query.trim() ? 'Past prompts · matching' : 'Past prompts · most recent'}
    </div>
    {items.length === 0 && (
      <div className="al-magic-suggest__row al-magic-suggest__row_empty">
        No past prompt contains what you typed.
      </div>
    )}
    {items.map((item, i) => {
      const [before, match, after] = highlight(item, query);
      return (
        <div
          key={item}
          role="option"
          aria-selected={i === activeIndex}
          title={item}
          className={`al-magic-suggest__row${i === activeIndex ? ' al-magic-suggest__row_active' : ''}`}
          onMouseMove={() => onHover(i)}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(item);
          }}
        >
          {before}
          {match && <b style={{ color: TM.blue }}>{match}</b>}
          {after}
        </div>
      );
    })}
    {items.length > 0 && (
      <div className="al-magic-suggest__foot">
        <kbd>↑</kbd>
        <kbd>↓</kbd> navigate · <kbd>Enter</kbd> insert · <kbd>Esc</kbd> close
      </div>
    )}
  </div>
);
